import React from "react";
import styled from "styled-components";
import { typography, spacing } from "../../styles/designSystem";

/**
 * Shared diagram primitives.
 *
 * Both case-study diagram sets draw from the same small vocabulary — a bordered
 * panel, a tracked-out label, a flow arrow, a caption — so a change to the frame
 * lands everywhere rather than in one file. Exactly the composition-boundary
 * argument the homepage makes, applied here.
 */

export const Panel = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.5);
    padding: ${spacing[700]} ${spacing[600]};
    width: 100%;
    min-width: 0;
`;

export const PanelLabel = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ theme }) => theme.subtleText};
    padding-bottom: ${spacing[300]};
    margin-bottom: ${spacing[600]};
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
`;

export const Arrow = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.subtleText};
    font-size: ${typography.fontSize[300]};
    line-height: 1;
    padding: ${spacing[200]} 0;
`;

export const Caption = styled.p`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: ${spacing[500]} 0 0;
`;

/** A single labelled stage in a vertical pipeline. */
export const Stage = styled.div`
    border: 1px solid ${({ color }) => color};
    border-left-width: 3px;
    padding: ${spacing[400]} ${spacing[500]};
    display: flex;
    align-items: baseline;
    gap: ${spacing[400]};
    flex-wrap: wrap;
`;

export const StageName = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    min-width: 11rem;
`;

export const StageDesc = styled.span`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[400]};
    color: ${({ theme }) => theme.subtleText};
    flex: 1;
    min-width: 0;
`;

// ===== Domain grid =====
// "What the work spanned" appears on more than one case study, so the shape lives
// here and each page supplies its own domains.
const DomainCells = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${spacing[400]};

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const DomainCell = styled.div`
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-top: 2px solid ${({ color }) => color};
    padding: ${spacing[500]};
    min-width: 0;
`;

const DomainName = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[300]};
`;

const DomainDesc = styled.p`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0;
`;

export const DomainGrid = ({ label, domains }) => (
    <Panel>
        <PanelLabel>{label}</PanelLabel>
        <DomainCells>
            {domains.map((d) => (
                <DomainCell key={d.name} color={d.color}>
                    <DomainName color={d.color}>{d.name}</DomainName>
                    <DomainDesc>{d.desc}</DomainDesc>
                </DomainCell>
            ))}
        </DomainCells>
    </Panel>
);

// ===== Vertical pipeline =====
// Repeated across four case studies (system layers, validation stages, data flow),
// so the shape lives here and each page supplies its stages.
export const PipelineFlow = ({ label, stages, caption }) => (
    <Panel>
        <PanelLabel>{label}</PanelLabel>
        {stages.map((stage, i) => (
            <React.Fragment key={stage.name}>
                <Stage color={stage.color}>
                    <StageName color={stage.color}>{stage.name}</StageName>
                    {stage.desc && <StageDesc>{stage.desc}</StageDesc>}
                </Stage>
                {i < stages.length - 1 && <Arrow aria-hidden="true">&#8595;</Arrow>}
            </React.Fragment>
        ))}
        {caption && <Caption>{caption}</Caption>}
    </Panel>
);

// ===== Numbered step grid =====
const StepCells = styled.div`
    display: grid;
    grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
    align-items: stretch;
    gap: ${spacing[200]};

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const StepCell = styled.div`
    border: 1px solid ${({ color }) => color};
    border-left-width: 3px;
    padding: ${spacing[400]} ${spacing[300]};
    display: flex;
    flex-direction: column;
    gap: ${spacing[150]};
    min-width: 0;
`;

const StepIndex = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[100]};
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ color }) => color};
`;

const StepLabel = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.normal};
    color: ${({ theme }) => theme.text};
    word-break: break-word;
`;

export const StepGrid = ({ label, steps, columns = 5, caption }) => (
    <Panel>
        <PanelLabel>{label}</PanelLabel>
        <StepCells $columns={columns}>
            {steps.map((step, i) => (
                <StepCell key={step.name} color={step.color}>
                    <StepIndex color={step.color}>{String(i + 1).padStart(2, "0")}</StepIndex>
                    <StepLabel>{step.name}</StepLabel>
                </StepCell>
            ))}
        </StepCells>
        {caption && <Caption>{caption}</Caption>}
    </Panel>
);

// ===== Contrast pair =====
// Several of the strongest moments in these case studies are reframes rather than
// claims, and a reframe reads better as a before/after than as a sentence.
const PairGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[400]};
    margin: ${spacing[700]} 0;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const PairSide = styled.div`
    border: 1px solid ${({ color }) => color};
    border-top-width: 3px;
    padding: ${spacing[500]};
    min-width: 0;
`;

const PairLabel = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[100]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[300]};
`;

const PairText = styled.div`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
`;

export const ContrastPair = ({ left, right }) => (
    <PairGrid>
        <PairSide color={left.color}>
            <PairLabel color={left.color}>{left.label}</PairLabel>
            <PairText>{left.text}</PairText>
        </PairSide>
        <PairSide color={right.color}>
            <PairLabel color={right.color}>{right.label}</PairLabel>
            <PairText>{right.text}</PairText>
        </PairSide>
    </PairGrid>
);

// ===== Cycle =====
const CycleRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${spacing[300]};
`;

const CycleNode = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color, theme }) => color || theme.text};
    border: 1px solid ${({ color, theme }) => color || theme.borderMedium};
    padding: ${spacing[300]} ${spacing[400]};
    white-space: nowrap;
`;

const CycleArrow = styled.span`
    color: ${({ theme }) => theme.primary};
    opacity: 0.6;
`;

/**
 * Node and its trailing arrow travel together, so a wrapped row never opens with a
 * dangling connector — the arrow stays at the end of the line it belongs to.
 */
const CycleItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: ${spacing[300]};
`;

const CycleNote = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    letter-spacing: ${typography.letterSpacing.wide};
    text-transform: uppercase;
    color: ${({ color }) => color};
    margin-top: ${spacing[400]};

    &::before {
        content: "\\21BA  ";
    }
`;

export const CycleFlow = ({ label, nodes, note, noteColor, caption }) => (
    <Panel>
        <PanelLabel>{label}</PanelLabel>
        <CycleRow>
            {nodes.map((node, i) => (
                <CycleItem key={node.name}>
                    <CycleNode color={node.color}>{node.name}</CycleNode>
                    {i < nodes.length - 1 && <CycleArrow aria-hidden="true">&#8250;</CycleArrow>}
                </CycleItem>
            ))}
        </CycleRow>
        {note && <CycleNote color={noteColor}>{note}</CycleNote>}
        {caption && <Caption>{caption}</Caption>}
    </Panel>
);
